import React, { useEffect, useState } from 'react';
import './Home.css';
import heroImg from '../logo-assets/GIEPEI - COMPACTO-ok.png';
import { db, auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import CertificateSection from '../components/CertificateSection';

interface Article {
  id: string;
  titulo: string;
  autor: string;
  conteudo: string;
  data: any;
  imagens?: string[];
}

const Home: React.FC = () => {
  const [featuredArticles, setFeaturedArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(auth.currentUser);
  const [heroData, setHeroData] = useState({
    title: 'Estudo e Pesquisa em Educação de Infâncias',
    description: 'O GIEPEI é um grupo internacional que promove a cooperação académica e científica entre instituições de Angola e do Brasil para fortalecer a formação de educadores de infância, a investigação educacional e a articulação institucional.'
  });
  const [counters, setCounters] = useState({
    integrantes: 0,
    pesquisasProduzidas: 0,
    pesquisasAndamento: 0,
    instituicoes: 0
  });

  const stripHtml = (html: string) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const getFirstImage = (article: Article) => {
    // 1. Buscar etiqueta img en el contenido HTML
    const imgRegex = /<img[^>]+src="([^">]+)"/;
    const match = article.conteudo.match(imgRegex);
    if (match && match[1]) return match[1];

    // 2. Buscar en el array de imágenes
    if (article.imagens && article.imagens.length > 0) {
      return article.imagens[0];
    }

    return null;
  };

  useEffect(() => {
    // Escuchar cambios de autenticación
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });

    const fetchHomeData = async () => {
      try {
        const q = query(collection(db, 'articles'), orderBy('data', 'desc'), limit(3));
        const querySnapshot = await getDocs(q);
        setFeaturedArticles(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Article)));

        const settingsSnap = await getDoc(doc(db, 'settings', 'main'));
        let integrantesAutoCount = 0;
        try {
          const qMembers = await getDocs(collection(db, 'integrantes_giepei'));
          integrantesAutoCount = qMembers.size;
        } catch (mErr) { console.error("Erro ao contar integrantes:", mErr); }

        if (settingsSnap.exists()) {
          const data = settingsSnap.data();
          setHeroData({
            title: data.heroTitle || heroData.title,
            description: data.heroDescription || heroData.description
          });
          setCounters({
            integrantes: integrantesAutoCount || data.countIntegrantes || 0,
            pesquisasProduzidas: data.countPesquisasProduzidas || 0,
            pesquisasAndamento: data.countPesquisasAndamento || 0,
            instituicoes: data.countInstituicoes || 0
          });
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };

    fetchHomeData();
    return () => unsubscribe();
  }, []);

  return (
    <div className="home-page">
      <section className="hero">
        <div className="container hero-container">
          <div className="hero-content">
            <h1>{heroData.title}</h1>
            <p>{heroData.description}</p>
            <div className="hero-btns">
              <Link to="/publicacoes" className="btn btn-primary">Ver Publicações</Link>
              <Link to="/sobre-nosotros" className="btn btn-secondary">Saber Mais</Link>
            </div>
          </div>
          <div className="hero-image">
            <img src={heroImg} alt="GIEPEI" />
          </div>
        </div>
      </section>

      {currentUser && (
        <div className="container" style={{ marginTop: '-2rem', position: 'relative', zIndex: 10 }}>
          <CertificateSection />
        </div>
      )}

      {/* Sección de Contadores */}
      {(counters.integrantes > 0 || counters.pesquisasProduzidas > 0 || counters.pesquisasAndamento > 0 || counters.instituicoes > 0) && (
        <section className="counters-section container">
          <div className="counters-wrapper">
            <div className="counters-track">
              {[...Array(2)].map((_, i) => (
                <React.Fragment key={i}>
                  {counters.integrantes > 0 && (
                    <div className="counter-item">
                      <span className="counter-number">{counters.integrantes}</span>
                      <span className="counter-label">Integrantes</span>
                    </div>
                  )}
                  {counters.pesquisasProduzidas > 0 && (
                    <div className="counter-item">
                      <span className="counter-number">{counters.pesquisasProduzidas}</span>
                      <span className="counter-label">Investigações Produzidas</span>
                    </div>
                  )}
                  {counters.pesquisasAndamento > 0 && (
                    <div className="counter-item">
                      <span className="counter-number">{counters.pesquisasAndamento}</span>
                      <span className="counter-label">Investigações em Andamento</span>
                    </div>
                  )}
                  {counters.instituicoes > 0 && (
                    <div className="counter-item">
                      <span className="counter-number">{counters.instituicoes}</span>
                      <span className="counter-label">Instituições Parceiras</span>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="featured-content container">
        <div className="section-title"><h2>Publicações Recentes</h2><div className="underline"></div></div>
        {loading ? <p style={{textAlign: 'center'}}>Carregando...</p> : (
          <div className="content-grid">
            {featuredArticles.map(article => {
              const firstImage = getFirstImage(article);
              return (
                <div key={article.id} className="content-card">
                  {firstImage && (
                    <div className="card-image"><img src={firstImage} alt={article.titulo} /></div>
                  )}
                  <div className="card-body">
                    <h3>{article.titulo}</h3>
                    <p className="author-tag">Por: {article.autor}</p>
                    <p className="excerpt">{stripHtml(article.conteudo).substring(0, 150)}...</p>
                    <Link to={`/publicacoes/${article.id}`} className="read-more">Ler mais &rarr;</Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default Home;
